import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormGroup,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import PublicShareActions from "../../../platform/components/common/PublicShareActions";
import SeoMeta from "../../../platform/components/common/seoMeta";
import { SEND_EMAIL_MUTATION } from "../../../platform/graphql/mutations/business";
import { CREATE_REFERRAL_LEAD_BROADCAST } from "../../../platform/graphql/mutations/care";
import { GET_ALL_COUNTRIES } from "../../../platform/graphql/queries/countryStateCity";
import { buildCareReferShareMessage } from "../../../platform/util/publicShareMessages";
import CareResidentDetailsFields, {
  type CareResidentDetailsFieldValue,
} from "../components/CareResidentDetailsFields";

const SUPPORT_EMAIL = "support@plany365.com";
const NO_REPLY_EMAIL = "no-reply@plany365.com";
const SHARE_PATH = "/referrer/start";
const PUBLIC_SHARE_PATH = "/referrer";
const DEFAULT_COUNTRY_ID = 228;
const DEFAULT_RADIUS_MILES = 15;
const DEFAULT_MAX_INVITES = 5;
const AGENT_SOURCED_SOURCE = "AGENT_SOURCED";
const PLATFORM_DIRECT_SOURCE = "PLATFORM_DIRECT";

const CARE_NEED_OPTIONS = [
  "Memory care (Dementia / Alzheimer's)",
  "Mobility help (wheelchair / walking)",
  "Medication assistance",
  "Full care needed",
] as const;

const PAYER_OPTIONS = [
  { label: "Private Pay", value: "PRIVATE" },
  { label: "Medicaid", value: "MEDICAID" },
  { label: "Not sure", value: "UNKNOWN" },
] as const;

const TIMELINE_OPTIONS = [
  "ASAP (within days)",
  "Within 1-2 weeks",
  "Just exploring",
] as const;

const FORM_STEPS = [
  "Contact & location",
  "Care details",
  "Preferences",
  "Review & submit",
] as const;

const FORM_STEP_SUBTITLES = [
  "Share the best contact and where care is needed.",
  "Add the main care needs, payment details, and timing.",
  "Add any preferred area or room details.",
  "Review the request and send it when it looks right.",
] as const;

type CareNeed = (typeof CARE_NEED_OPTIONS)[number];
type CareFindPayerType = (typeof PAYER_OPTIONS)[number]["value"];
type CareFindAttributionSourceType =
  | typeof AGENT_SOURCED_SOURCE
  | typeof PLATFORM_DIRECT_SOURCE;

type CareFindFormState = {
  fullName: string;
  phoneNumber: string;
  email: string;
  residentAge: string;
  gender: string;
  currentCountryId: number | null;
  currentStateId: number | null;
  currentCityId: number | null;
  currentZipCode: string;
  currentSituation: string;
  careNeeds: CareNeed[];
  notes: string;
  payerType: CareFindPayerType | "";
  budget: string;
  timeline: string;
  preferredCountryId: number | null;
  preferredStateId: number | null;
  preferredCityId: number | null;
  preferredZipCode: string;
  privateRoomNeeded: string;
  caregiverPreference: string;
};

type CountryCity = { id: number | string; name: string };
type CountryState = { id: number | string; name: string; cities?: CountryCity[] };
type Country = { id: number | string; name: string; states?: CountryState[] };
type LocationSelection = {
  countryId: number | null;
  stateId: number | null;
  cityId: number | null;
  zipCode: string;
};

type ResolvedLocationFilter = {
  label: string;
  filter: {
    countryId?: number;
    stateId?: number;
    cityId?: number;
    zip?: string;
    radiusMiles?: number;
    mustBeClaimed: boolean;
    maxInvites: number;
  };
};

function StepSectionCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid rgba(20,50,74,0.10)",
        p: { xs: 1.5, sm: 2 },
        bgcolor: "#ffffff",
      }}
    >
      <Stack spacing={1.5}>
        <SectionTitle title={title} subtitle={subtitle} />
        {children}
      </Stack>
    </Paper>
  );
}

const INITIAL_FORM: CareFindFormState = {
  fullName: "",
  phoneNumber: "",
  email: "",
  residentAge: "",
  gender: "",
  currentCountryId: null,
  currentStateId: null,
  currentCityId: null,
  currentZipCode: "",
  currentSituation: "",
  careNeeds: [],
  notes: "",
  payerType: "",
  budget: "",
  timeline: "",
  preferredCountryId: null,
  preferredStateId: null,
  preferredCityId: null,
  preferredZipCode: "",
  privateRoomNeeded: "",
  caregiverPreference: "",
};

function buildLeadReference() {
  const randomBlock = () => Math.random().toString(36).slice(2, 6).toUpperCase();
  return `CF-${randomBlock()}-${randomBlock()}`;
}

function isLikelyZipCode(value?: string | null) {
  return /^\d{5}(?:-\d{4})?$/.test(String(value || "").trim());
}

function parseCareFindPrefill(search: string) {
  const params = new URLSearchParams(search || "");
  const rawLocation = String(params.get("prefillLocation") || "").trim();
  const payerType = String(params.get("prefillPayer") || "").trim().toUpperCase();
  const timeline = String(params.get("prefillTimeline") || "").trim();
  const careNeeds = params
    .getAll("prefillNeed")
    .map((value) => String(value || "").trim())
    .filter((value): value is CareNeed =>
      (CARE_NEED_OPTIONS as readonly string[]).includes(value)
    );

  return {
    locationHint: rawLocation,
    zipCode: isLikelyZipCode(rawLocation) ? rawLocation : "",
    payerType: (PAYER_OPTIONS.some((option) => option.value === payerType)
      ? payerType
      : "") as CareFindPayerType | "",
    timeline: (TIMELINE_OPTIONS as readonly string[]).includes(timeline)
      ? timeline
      : "",
    careNeeds,
  };
}

function isValidEmail(value: string) {
  return /\S+@\S+\.\S+/.test(String(value || "").trim());
}

function isValidPhone(value: string) {
  return String(value || "").replace(/\D/g, "").length >= 7;
}

function joinValue(value?: string | null, fallback = "-") {
  const trimmed = String(value || "").trim();
  return trimmed || fallback;
}

function formatList(values: string[]) {
  return values.length ? values.join(", ") : "-";
}

function eqId(a: unknown, b: unknown) {
  return Number(a) === Number(b);
}

function asSelectValue(id?: number | null) {
  return id != null ? String(id) : "";
}

function getLocationSelection(
  form: CareFindFormState,
  scope: "current" | "preferred"
): LocationSelection {
  if (scope === "preferred") {
    return {
      countryId: form.preferredCountryId,
      stateId: form.preferredStateId,
      cityId: form.preferredCityId,
      zipCode: form.preferredZipCode,
    };
  }

  return {
    countryId: form.currentCountryId,
    stateId: form.currentStateId,
    cityId: form.currentCityId,
    zipCode: form.currentZipCode,
  };
}

function getSelectedCountry(countries: Country[], countryId: number | null) {
  return countryId != null
    ? countries.find((country) => eqId(country.id, countryId)) ?? null
    : null;
}

function getSelectedState(country: Country | null, stateId: number | null) {
  return stateId != null
    ? country?.states?.find((state) => eqId(state.id, stateId)) ?? null
    : null;
}

function getSelectedCity(state: CountryState | null, cityId: number | null) {
  return cityId != null
    ? state?.cities?.find((city) => eqId(city.id, cityId)) ?? null
    : null;
}

function hasLocationTarget(location: LocationSelection) {
  return Boolean(location.cityId || String(location.zipCode || "").trim());
}

function buildLocationSummary(
  location: LocationSelection,
  countries: Country[] = [],
  options?: { includeCountry?: boolean; fallback?: string }
) {
  const includeCountry = options?.includeCountry ?? true;
  const fallback = options?.fallback ?? "-";
  const country = getSelectedCountry(countries, location.countryId);
  const state = getSelectedState(country, location.stateId);
  const city = getSelectedCity(state, location.cityId);
  const zipCode = String(location.zipCode || "").trim();
  const parts = [city?.name, state?.name];

  if (includeCountry && country?.name) {
    parts.push(country.name);
  } else if (!parts.filter(Boolean).length && country?.name) {
    parts.push(country.name);
  }

  const baseLabel = parts.filter(Boolean).join(", ");
  const fullLabel = [baseLabel, zipCode].filter(Boolean).join(" ").trim();

  return fullLabel || fallback;
}

export function resolveCareFindLocationFilter(
  form: CareFindFormState,
  countries: Country[] = []
): ResolvedLocationFilter | null {
  const preferredLocation = getLocationSelection(form, "preferred");
  const currentLocation = getLocationSelection(form, "current");
  const selectedLocation = hasLocationTarget(preferredLocation) ? preferredLocation : currentLocation;
  const country = getSelectedCountry(countries, selectedLocation.countryId);
  const state = getSelectedState(country, selectedLocation.stateId);
  const city = getSelectedCity(state, selectedLocation.cityId);
  const zipCode = String(selectedLocation.zipCode || "").trim();

  if (!city?.id && !zipCode) {
    return null;
  }

  const useZipRouting = Boolean(zipCode);

  return {
    label: buildLocationSummary(selectedLocation, countries),
    filter: {
      countryId:
        !useZipRouting && country?.id != null ? Number(country.id) : undefined,
      stateId: !useZipRouting && state?.id != null ? Number(state.id) : undefined,
      cityId: !useZipRouting && city?.id != null ? Number(city.id) : undefined,
      zip: useZipRouting ? zipCode : undefined,
      radiusMiles: useZipRouting ? DEFAULT_RADIUS_MILES : undefined,
      mustBeClaimed: true,
      maxInvites: DEFAULT_MAX_INVITES,
    },
  };
}

function getPayerTypeLabel(payerType?: CareFindPayerType | "" | null) {
  return (
    PAYER_OPTIONS.find((option) => option.value === payerType)?.label ||
    (payerType === "UNKNOWN" ? "Not sure" : "")
  );
}

function resolvePayerType(payerType?: CareFindPayerType | ""): CareFindPayerType {
  if (payerType === "PRIVATE" || payerType === "MEDICAID") return payerType;
  return "UNKNOWN";
}

function getPricingModeForPayerType(payerType: CareFindPayerType) {
  return payerType === "PRIVATE" ? "CONCIERGE" : "SELF_SERVE";
}

function getCustomerFollowUpMessage(payerType: CareFindPayerType) {
  if (payerType === "PRIVATE") {
    return "Our team will contact you within 15 minutes.";
  }
  if (payerType === "MEDICAID") {
    return "Available care providers may contact you directly.";
  }
  return "We will review your request and follow up soon.";
}

function getCareFindAttribution(search: string) {
  const searchParams = new URLSearchParams(search || "");
  const sourceAgentId = String(searchParams.get("agentId") || "").trim() || null;
  const sourceAgentName = String(searchParams.get("agentName") || "").trim() || null;
  const rawSourceType = String(
    searchParams.get("leadSourceType") || searchParams.get("sourceType") || ""
  )
    .trim()
    .toUpperCase();

  let leadSourceType: CareFindAttributionSourceType = PLATFORM_DIRECT_SOURCE;
  if (
    rawSourceType === AGENT_SOURCED_SOURCE ||
    rawSourceType === PLATFORM_DIRECT_SOURCE
  ) {
    leadSourceType = rawSourceType as CareFindAttributionSourceType;
  } else if (sourceAgentId || sourceAgentName) {
    leadSourceType = AGENT_SOURCED_SOURCE;
  }

  const attributionMethod =
    leadSourceType === AGENT_SOURCED_SOURCE
      ? sourceAgentId || sourceAgentName
        ? "TRACKED_AGENT_LINK"
        : "MANUAL_AGENT_CONFIRMATION"
      : "CARE_FIND_FORM";

  return {
    leadSourceType,
    sourceAgentId,
    sourceAgentName,
    attributionMethod,
  };
}

function mapTimelineToUrgency(timeline: string) {
  if (timeline === "ASAP (within days)") return "IMMEDIATE";
  if (timeline === "Within 1-2 weeks") return "WEEK";
  return "UNKNOWN";
}

function mapTimelineToMoveInChoice(timeline: string) {
  if (timeline === "ASAP (within days)") return "ASAP";
  if (timeline === "Within 1-2 weeks") return "WEEK";
  return "UNKNOWN";
}

function buildTrackingSummary(
  referenceId: string,
  internalTrackPath: string | null,
  inviteCount: number | null
) {
  const parts = [`Reference: ${referenceId}`];
  if (internalTrackPath) parts.push(`Internal tracking: ${internalTrackPath}`);
  if (typeof inviteCount === "number") {
    parts.push(`Invited providers: ${inviteCount}`);
  }
  return parts.join("\n");
}

function buildCareFindEmailText(
  form: CareFindFormState,
  options?: {
    referenceId?: string | null;
    internalTrackPath?: string | null;
    inviteCount?: number | null;
    resolvedLocationLabel?: string | null;
    currentLocationLabel?: string | null;
    preferredLocationLabel?: string | null;
  }
) {
  return [
    "New care intake submission from /referrer/start",
    "",
    options?.referenceId ? buildTrackingSummary(
      options.referenceId,
      options.internalTrackPath ?? null,
      options.inviteCount ?? null
    ) : null,
    options?.resolvedLocationLabel ? `Resolved route location: ${options.resolvedLocationLabel}` : null,
    options?.referenceId || options?.resolvedLocationLabel ? "" : null,
    "Contact Info",
    `Full Name: ${joinValue(form.fullName)}`,
    `Phone Number: ${joinValue(form.phoneNumber)}`,
    `Email: ${joinValue(form.email)}`,
    "",
    "Resident Info",
    `Resident Age: ${joinValue(form.residentAge)}`,
    `Gender: ${joinValue(form.gender)}`,
    `Current Location: ${joinValue(options?.currentLocationLabel)}`,
    `Current Situation: ${joinValue(form.currentSituation)}`,
    "",
    "Care Needs",
    `Needs: ${formatList(form.careNeeds)}`,
    `Anything else we should know?: ${joinValue(form.notes)}`,
    "",
    "Payment",
    `Payer / Funding Source: ${joinValue(getPayerTypeLabel(form.payerType))}`,
    `Budget: ${joinValue(form.budget)}`,
    "",
    "Timeline",
    `Timeline: ${joinValue(form.timeline)}`,
    "",
    "Preferences",
    `Preferred city/location: ${joinValue(options?.preferredLocationLabel)}`,
    `Private room needed?: ${joinValue(form.privateRoomNeeded)}`,
    `Male/Female caregiver preference: ${joinValue(form.caregiverPreference)}`,
  ]
    .filter(Boolean)
    .join("\n");
}

function buildCustomerConfirmationText(
  form: CareFindFormState,
  referenceId: string,
  currentLocationLabel: string
) {
  const payerType = resolvePayerType(form.payerType);
  return [
    `Hello ${joinValue(form.fullName, "there")},`,
    "",
    `Thank you. ${getCustomerFollowUpMessage(payerType)}`,
    "",
    "We received your care request with the following details:",
    `Location: ${joinValue(currentLocationLabel)}`,
    `Payer / Funding Source: ${joinValue(getPayerTypeLabel(form.payerType))}`,
    `Timeline: ${joinValue(form.timeline)}`,
    `Budget: ${joinValue(form.budget)}`,
    `Reference: ${referenceId}`,
    "",
    "If you need to add anything else, reply to this email or call Plany Assist at (833) 368-1399 right away.",
  ].join("\n");
}

export default function CareFindPage(): JSX.Element {
  const referenceIdRef = React.useRef(buildLeadReference());
  const prefillAppliedRef = React.useRef(false);
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);
  const [createLeadBroadcast] = useMutation(CREATE_REFERRAL_LEAD_BROADCAST, {
    errorPolicy: "all",
    onError: () => { },
  });
  const countriesQuery = useQuery(GET_ALL_COUNTRIES, { fetchPolicy: "cache-first" });
  const [form, setForm] = React.useState<CareFindFormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [reviewConfirmed, setReviewConfirmed] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [submissionReference, setSubmissionReference] = React.useState<string | null>(null);
  const prefills = React.useMemo(
    () =>
      parseCareFindPrefill(
        typeof window === "undefined" ? "" : window.location.search
      ),
    []
  );
  const countries: Country[] = React.useMemo(
    () =>
      Array.isArray(countriesQuery.data?.getAllCountries)
        ? (countriesQuery.data.getAllCountries as Country[])
        : [],
    [countriesQuery.data?.getAllCountries]
  );
  const currentCountry = React.useMemo(
    () => getSelectedCountry(countries, form.currentCountryId),
    [countries, form.currentCountryId]
  );
  const currentState = React.useMemo(
    () => getSelectedState(currentCountry, form.currentStateId),
    [currentCountry, form.currentStateId]
  );
  const preferredCountry = React.useMemo(
    () => getSelectedCountry(countries, form.preferredCountryId),
    [countries, form.preferredCountryId]
  );
  const preferredState = React.useMemo(
    () => getSelectedState(preferredCountry, form.preferredStateId),
    [preferredCountry, form.preferredStateId]
  );
  const currentHasStates = Boolean(currentCountry?.states?.length);
  const currentHasCities = Boolean(currentState?.cities?.length);
  const preferredHasStates = Boolean(preferredCountry?.states?.length);
  const preferredHasCities = Boolean(preferredState?.cities?.length);
  const isMedicaidRequest = form.payerType === "MEDICAID";
  const currentFollowUpMessage = React.useMemo(
    () => getCustomerFollowUpMessage(resolvePayerType(form.payerType)),
    [form.payerType]
  );
  const totalSteps = FORM_STEPS.length;
  const isFinalStep = activeStep === totalSteps - 1;
  const currentStepLabel = FORM_STEPS[activeStep];
  const currentStepSubtitle = FORM_STEP_SUBTITLES[activeStep];
  const attribution = React.useMemo(
    () =>
      getCareFindAttribution(
        typeof window === "undefined" ? "" : window.location.search
      ),
    []
  );

  React.useEffect(() => {
    if (!countries.length) return;

    const hasDefaultCountry = countries.some((country: Country) =>
      eqId(country.id, DEFAULT_COUNTRY_ID)
    );
    if (!hasDefaultCountry) return;

    setForm((prev) => {
      const nextCurrentCountryId = prev.currentCountryId ?? DEFAULT_COUNTRY_ID;
      const nextPreferredCountryId = prev.preferredCountryId ?? DEFAULT_COUNTRY_ID;

      if (
        nextCurrentCountryId === prev.currentCountryId &&
        nextPreferredCountryId === prev.preferredCountryId
      ) {
        return prev;
      }

      return {
        ...prev,
        currentCountryId: nextCurrentCountryId,
        preferredCountryId: nextPreferredCountryId,
      };
    });
  }, [countries]);

  React.useEffect(() => {
    if (prefillAppliedRef.current) return;

    setForm((prev) => {
      let changed = false;
      const next = { ...prev };

      if (prefills.zipCode && !prev.currentZipCode.trim()) {
        next.currentZipCode = prefills.zipCode;
        changed = true;
      }
      if (prefills.payerType && !prev.payerType) {
        next.payerType = prefills.payerType;
        changed = true;
      }
      if (prefills.timeline && !prev.timeline.trim()) {
        next.timeline = prefills.timeline;
        changed = true;
      }
      if (prefills.careNeeds.length && !prev.careNeeds.length) {
        next.careNeeds = prefills.careNeeds;
        changed = true;
      }

      return changed ? next : prev;
    });

    prefillAppliedRef.current = true;
  }, [prefills]);

  const shareUrl = React.useMemo(() => {
    if (typeof window === "undefined") return `https://plany365.com${PUBLIC_SHARE_PATH}`;
    return `${window.location.origin}${PUBLIC_SHARE_PATH}${window.location.search || ""}`;
  }, []);
  const reviewSummary = React.useMemo(
    () => [
      {
        label: "Contact",
        value: [form.fullName.trim(), form.phoneNumber.trim()].filter(Boolean).join(" • ") || "-",
      },
      {
        label: "Current location",
        value: buildLocationSummary(getLocationSelection(form, "current"), countries),
      },
      {
        label: "Care needs",
        value: form.careNeeds.length ? form.careNeeds.join(", ") : "Not specified",
      },
      {
        label: "Payer",
        value: getPayerTypeLabel(form.payerType) || "-",
      },
      {
        label: "Timeline",
        value: form.timeline.trim() || "-",
      },
    ],
    [countries, form]
  );
  const finalReviewSummary = React.useMemo(
    () => [
      ...reviewSummary.map((item) =>
        item.label === "Contact"
          ? { ...item, value: item.value.replace("â€¢", "|") }
          : item
      ),
      {
        label: "Preferred area",
        value: hasLocationTarget(getLocationSelection(form, "preferred"))
          ? buildLocationSummary(getLocationSelection(form, "preferred"), countries)
          : "No preference added",
      },
    ],
    [countries, form, reviewSummary]
  );
  const socialShareMessage = React.useMemo(
    () => buildCareReferShareMessage(shareUrl),
    [shareUrl],
  );

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target;
      setForm((prev) => ({ ...prev, [name]: value }));
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
      setError(null);
    },
    []
  );

  const handleResidentDetailsFieldsChange = React.useCallback(
    (next: Partial<CareResidentDetailsFieldValue>) => {
      setForm((prev) => ({
        ...prev,
        residentAge: next.residentAge ?? prev.residentAge,
        gender: next.gender ?? prev.gender,
        currentSituation: next.currentSituation ?? prev.currentSituation,
        budget: next.budget ?? prev.budget,
        privateRoomNeeded: next.privateRoomNeeded ?? prev.privateRoomNeeded,
        caregiverPreference: next.caregiverPreference ?? prev.caregiverPreference,
      }));
      setFieldErrors((prev) => ({
        ...prev,
        budget: Object.prototype.hasOwnProperty.call(next, "budget") ? "" : prev.budget,
      }));
      setError(null);
    },
    []
  );

  const handlePayerTypeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target;
      setForm((prev) => ({ ...prev, payerType: value as CareFindPayerType | "" }));
      setFieldErrors((prev) => ({ ...prev, payerType: "", budget: "" }));
      setError(null);
    },
    []
  );

  const handleLocationSelectChange = React.useCallback(
    (fieldName: keyof CareFindFormState, value: number | null, clearedKeys: string[] = []) => {
      setForm((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
      if (clearedKeys.length) {
        setFieldErrors((prev) => {
          const nextErrors = { ...prev };
          clearedKeys.forEach((key) => {
            nextErrors[key] = "";
          });
          return nextErrors;
        });
      }
      setError(null);
    },
    []
  );

  const handleLocationTextChange = React.useCallback(
    (fieldName: keyof CareFindFormState, value: string, clearedKeys: string[] = []) => {
      setForm((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
      if (clearedKeys.length) {
        setFieldErrors((prev) => {
          const nextErrors = { ...prev };
          clearedKeys.forEach((key) => {
            nextErrors[key] = "";
          });
          return nextErrors;
        });
      }
      setError(null);
    },
    []
  );

  const handleCareNeedChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value, checked } = event.target;
      setForm((prev) => ({
        ...prev,
        careNeeds: checked
          ? [...prev.careNeeds, value as CareNeed]
          : prev.careNeeds.filter((item) => item !== value),
      }));
      setError(null);
    },
    []
  );

  const validateStep = React.useCallback((step: number) => {
    const nextErrors: Record<string, string> = {};
    const payerType = resolvePayerType(form.payerType);
    const requiresBudget = payerType !== "MEDICAID";
    const hasZip = Boolean(form.currentZipCode.trim());

    if (step === 0) {
      if (!form.fullName.trim()) nextErrors.fullName = "Full name is required.";
      if (!form.phoneNumber.trim()) {
        nextErrors.phoneNumber = "Phone number is required.";
      } else if (!isValidPhone(form.phoneNumber)) {
        nextErrors.phoneNumber = "Enter a valid phone number.";
      }
      if (form.email.trim() && !isValidEmail(form.email)) {
        nextErrors.email = "Enter a valid email or leave it blank.";
      }
      if (!form.currentCountryId) nextErrors.currentCountryId = "Country is required.";
      if (!hasZip && currentHasStates && !form.currentStateId) {
        nextErrors.currentStateId = "State is required unless ZIP is provided.";
      }
      if (!hasZip && currentHasCities && !form.currentCityId) {
        nextErrors.currentCityId = "City is required unless ZIP is provided.";
      }
      if (!hasZip && !form.currentCityId) {
        nextErrors.currentZipCode = "Enter a ZIP code or choose a city.";
      }
    }

    if (step === 1) {
      if (!form.payerType) nextErrors.payerType = "Payment type is required.";
      if (requiresBudget && !form.budget.trim()) {
        nextErrors.budget = "Budget is required unless this is Medicaid.";
      }
      if (!form.timeline.trim()) nextErrors.timeline = "Timeline is required.";
    }

    return nextErrors;
  }, [currentHasCities, currentHasStates, form]);

  const validate = React.useCallback(
    () => ({
      ...validateStep(0),
      ...validateStep(1),
    }),
    [validateStep]
  );

  const getFirstStepWithErrors = React.useCallback((errors: Record<string, string>) => {
    const stepOneFields = [
      "fullName",
      "phoneNumber",
      "email",
      "currentCountryId",
      "currentStateId",
      "currentCityId",
      "currentZipCode",
    ];
    const stepTwoFields = ["payerType", "budget", "timeline"];

    if (stepOneFields.some((field) => errors[field])) return 0;
    if (stepTwoFields.some((field) => errors[field])) return 1;
    return 2;
  }, []);

  const handleNextStep = React.useCallback(() => {
    const nextErrors = validateStep(activeStep);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setError("Please complete the required fields before moving on.");
      return;
    }

    setFieldErrors({});
    setError(null);
    if (activeStep === totalSteps - 2) {
      setReviewConfirmed(false);
    }
    setActiveStep((prev) => Math.min(prev + 1, totalSteps - 1));
  }, [activeStep, totalSteps, validateStep]);

  const handlePreviousStep = React.useCallback(() => {
    setError(null);
    if (activeStep === totalSteps - 1) {
      setReviewConfirmed(false);
    }
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, [activeStep, totalSteps]);

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const nextErrors = validate();
      if (Object.keys(nextErrors).length > 0) {
        setFieldErrors(nextErrors);
        setError("Please complete the required fields before submitting.");
        setActiveStep(getFirstStepWithErrors(nextErrors));
        return;
      }

      setSubmitting(true);
      setError(null);

      try {
        const referenceId = referenceIdRef.current;
        const resolvedLocation = resolveCareFindLocationFilter(form, countries);
        const currentLocation = getLocationSelection(form, "current");
        const preferredLocation = getLocationSelection(form, "preferred");
        const payerType = resolvePayerType(form.payerType);
        const urgency = mapTimelineToUrgency(form.timeline);
        const careNeedSummary = form.careNeeds.length ? form.careNeeds.join(", ") : null;
        const currentLocationLabel = buildLocationSummary(currentLocation, countries);
        const preferredLocationLabel = hasLocationTarget(preferredLocation)
          ? buildLocationSummary(preferredLocation, countries)
          : "";
        const subjectLocationLabel = buildLocationSummary(currentLocation, countries, {
          includeCountry: false,
          fallback: "Location submitted",
        });

        if (!resolvedLocation) {
          setError("Please complete the location fields so we can route your request quickly.");
          return;
        }

        const extra = {
          patientKey: referenceId,
          residentKeyDisplay: referenceId,
          residentKeyCompare: referenceId,
          careNeed: careNeedSummary,
          payerType,
          pricing: { mode: getPricingModeForPayerType(payerType), isRush: false },
          termsAccepted: true,
          submittedVia: "BROADCAST",
          leadSource: {
            type: attribution.leadSourceType,
            sourceAgentId:
              attribution.leadSourceType === AGENT_SOURCED_SOURCE
                ? attribution.sourceAgentId
                : null,
            sourceAgentName:
              attribution.leadSourceType === AGENT_SOURCED_SOURCE
                ? attribution.sourceAgentName
                : null,
            attributionMethod: attribution.attributionMethod,
          },
          referrerContact: {
            name: form.fullName.trim(),
            email: form.email.trim() || null,
            phone: form.phoneNumber.trim(),
          },
          submittingOnBehalf: false,
          medicaidConfirmed: payerType === "MEDICAID" ? true : undefined,
          moveIn: { choice: mapTimelineToMoveInChoice(form.timeline), date: undefined },
          careFindForm: {
            residentAge: form.residentAge.trim() || null,
            gender: form.gender.trim() || null,
            currentLocation: currentLocationLabel,
            currentCountryId: form.currentCountryId,
            currentStateId: form.currentStateId,
            currentCityId: form.currentCityId,
            currentZipCode: form.currentZipCode.trim(),
            currentSituation: form.currentSituation.trim() || null,
            careNeeds: form.careNeeds,
            notes: form.notes.trim() || null,
            payerType,
            budget: form.budget.trim(),
            timeline: form.timeline.trim(),
            preferredLocation: preferredLocationLabel || null,
            preferredCountryId: form.preferredCountryId,
            preferredStateId: form.preferredStateId,
            preferredCityId: form.preferredCityId,
            preferredZipCode: form.preferredZipCode.trim() || null,
            privateRoomNeeded: form.privateRoomNeeded.trim() || null,
            caregiverPreference: form.caregiverPreference.trim() || null,
          },
          location: {
            submittedCurrentLocation: currentLocationLabel,
            submittedPreferredLocation: preferredLocationLabel || null,
            resolvedLabel: resolvedLocation.label,
          },
          submissionMeta: {
            submittedVia: "care-refer",
            path: SHARE_PATH,
            shareUrl,
            referenceId,
            trackedSourceAgentId: attribution.sourceAgentId,
            trackedSourceAgentName: attribution.sourceAgentName,
          },
        };

        const broadcastResult = await createLeadBroadcast({
          variables: {
            seed: {
              residentKey: referenceId,
              payerType,
              urgency,
              kind: "referral",
              careNeed: careNeedSummary,
              intakeNotes: form.notes.trim() || null,
              leadSourceType: attribution.leadSourceType,
              sourceAgentId:
                attribution.leadSourceType === AGENT_SOURCED_SOURCE
                  ? attribution.sourceAgentId
                  : null,
              intakeRoutingStatus: "ADMIN_REVIEW",
              routingMode: "ADMIN_ASSIGN",
              assignmentOrigin: "UNASSIGNED",
              extra,
            },
            filter: resolvedLocation.filter,
          },
        });

        let leadGroup =
          broadcastResult.data?.createReferralLeadBroadcast?.group ?? null;
        let inviteCount = Number.isFinite(
          Number(broadcastResult.data?.createReferralLeadBroadcast?.inviteCount)
        )
          ? Number(broadcastResult.data?.createReferralLeadBroadcast?.inviteCount)
          : null;

        if (!leadGroup?.id) {
          const retryResult = await createLeadBroadcast({
            variables: {
              seed: {
                residentKey: referenceId,
                payerType,
                urgency,
                kind: "referral",
                careNeed: careNeedSummary,
                intakeNotes: form.notes.trim() || null,
                leadSourceType: attribution.leadSourceType,
                sourceAgentId:
                  attribution.leadSourceType === AGENT_SOURCED_SOURCE
                    ? attribution.sourceAgentId
                    : null,
                intakeRoutingStatus: "ADMIN_REVIEW",
                routingMode: "ADMIN_ASSIGN",
                assignmentOrigin: "UNASSIGNED",
                extra,
              },
              filter: {
                ...resolvedLocation.filter,
                mustBeClaimed: false,
              },
            },
          });

          leadGroup = retryResult.data?.createReferralLeadBroadcast?.group ?? null;
          inviteCount = Number.isFinite(
            Number(retryResult.data?.createReferralLeadBroadcast?.inviteCount)
          )
            ? Number(retryResult.data?.createReferralLeadBroadcast?.inviteCount)
            : inviteCount;
        }

        if (!leadGroup?.id) {
          setError("We could not place your request in the queue right now. Please try again.");
          return;
        }

        const internalTrackPath = leadGroup?.id
          ? `/care/referrals/group/${leadGroup.id}`
          : null;

        await sendEmail({
          variables: {
            from_email: NO_REPLY_EMAIL,
            to: SUPPORT_EMAIL,
            subject: `[Care Find] ${form.fullName.trim()} - ${subjectLocationLabel}`,
            text: buildCareFindEmailText(form, {
              referenceId,
              internalTrackPath,
              inviteCount,
              resolvedLocationLabel: resolvedLocation.label,
              currentLocationLabel,
              preferredLocationLabel,
            }),
          },
        });

        if (form.email.trim()) {
          await sendEmail({
            variables: {
              from_email: NO_REPLY_EMAIL,
              to: form.email.trim(),
              subject: "We received your care request",
              text: buildCustomerConfirmationText(form, referenceId, currentLocationLabel),
            },
          });
        }

        setSubmissionReference(referenceId);
        setSubmitted(true);
      } catch (submitError: any) {
        setError(
          String(submitError?.message || "We could not submit the form right now. Please try again.")
        );
      } finally {
        setSubmitting(false);
      }
    },
    [attribution, countries, createLeadBroadcast, form, getFirstStepWithErrors, sendEmail, shareUrl, validate]
  );

  const handleFormSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      if (!isFinalStep) {
        event.preventDefault();
        handleNextStep();
        return;
      }

      if (!reviewConfirmed) {
        event.preventDefault();
        setError("Please confirm the review step before submitting.");
        return;
      }

      handleSubmit(event);
    },
    [handleNextStep, handleSubmit, isFinalStep, reviewConfirmed]
  );

  return (
    <>
      <SeoMeta
        title="Start a Resident Referral | Plany365"
        description="Share a resident placement request with Plany365."
        image="/og-preview.png"
        url={SHARE_PATH}
      />

      <Box
        sx={{
          minHeight: "100%",
          background:
            "linear-gradient(180deg, #f4fbff 0%, #ffffff 35%, #f9f8f3 100%)",
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 5 },
        }}
      >
        <Box sx={{ maxWidth: 860, mx: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              backgroundColor: "rgba(255,255,255,0.96)",
              boxShadow: "0 24px 60px rgba(18, 44, 67, 0.08)",
            }}
          >
            <Box
              sx={{
                px: { xs: 2.5, sm: 4 },
                py: { xs: 2.5, sm: 3.5 },
                background:
                  "linear-gradient(135deg, rgba(7,140,180,0.08) 0%, rgba(67,188,131,0.1) 100%)",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack spacing={1.25}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.1,
                    color: "#14324a",
                  }}
                >
                  Start a resident referral.
                </Typography>
                <Typography variant="body1" sx={{ color: "#31556f", fontWeight: 600 }}>
                  For individuals, families, care teams, and community partners
                  (for example: social workers, discharge planners, and case managers).
                </Typography>
                <Typography variant="body2" sx={{ color: "#557286" }}>
                  Share the essentials so the placement request can be reviewed quickly
                  and routed to the right next step.
                </Typography>
              </Stack>
            </Box>

            <Box
              component="form"
              onSubmit={handleFormSubmit}
              noValidate
              sx={{ px: { xs: 2.5, sm: 4 }, py: { xs: 2.5, sm: 3.5 } }}
            >
              <Stack spacing={3}>
                {submitted ? (
                  <Alert severity="success" sx={{ fontSize: 16 }}>
                    <Stack spacing={0.5}>
                      <Typography sx={{ fontWeight: 700 }}>
                        Thank you. {currentFollowUpMessage}
                      </Typography>
                      {submissionReference ? (
                        <Typography variant="body2">
                          Reference: {submissionReference}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Alert>
                ) : null}

                {error ? <Alert severity="error">{error}</Alert> : null}

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid rgba(20,50,74,0.10)",
                    bgcolor: "#fbfdff",
                    p: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                    >
                      <Box>
                        <Typography variant="overline" sx={{ fontWeight: 800, color: "#5d7888" }}>
                          Step {activeStep + 1} of {totalSteps}
                        </Typography>
                        <Typography sx={{ fontWeight: 800, color: "#14324a", fontSize: 18 }}>
                          {currentStepLabel}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#607a89" }}>
                          {currentStepSubtitle}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: "#607a89", fontWeight: 700 }}>
                        {Math.round(((activeStep + 1) / totalSteps) * 100)}% complete
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        height: 8,
                        borderRadius: 999,
                        bgcolor: "rgba(20,50,74,0.10)",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${((activeStep + 1) / totalSteps) * 100}%`,
                          height: "100%",
                          borderRadius: 999,
                          bgcolor: "#0d5b66",
                          transition: "width 180ms ease",
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: `repeat(${activeStep + 1}, minmax(180px, 1fr))`,
                          sm: `repeat(${activeStep + 1}, minmax(0, 1fr))`,
                        },
                        gap: 1,
                        overflowX: { xs: "auto", sm: "visible" },
                        pb: { xs: 0.5, sm: 0 },
                      }}
                    >
                      {FORM_STEPS.map((label, index) => {
                        if (index > activeStep) return null;
                        const isActive = index === activeStep;
                        const isComplete = index < activeStep;

                        return (
                          <Box key={label}>
                            <Box
                              sx={{
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: isActive
                                  ? "#0d5b66"
                                  : isComplete
                                    ? "rgba(15,122,90,0.24)"
                                    : "rgba(20,50,74,0.10)",
                                bgcolor: isActive
                                  ? "rgba(13,91,102,0.08)"
                                  : isComplete
                                    ? "rgba(15,122,90,0.08)"
                                    : "#ffffff",
                                px: 1.5,
                                py: 1.25,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: isActive ? "#0d5b66" : "#607a89",
                                  fontWeight: 800,
                                  mb: 0.25,
                                }}
                              >
                                {isComplete ? "Done" : `Step ${index + 1}`}
                              </Typography>
                              <Typography sx={{ fontWeight: 700, color: "#14324a" }}>
                                {label}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Stack>
                </Paper>

                {activeStep === 0 ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <StepSectionCard
                        title="Contact Info"
                        subtitle="Please share the best way to reach you."
                      >
                        <Grid container spacing={1.5}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              required
                              name="fullName"
                              label="Full Name"
                              value={form.fullName}
                              onChange={handleChange}
                              error={Boolean(fieldErrors.fullName)}
                              helperText={fieldErrors.fullName || " "}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              required
                              name="phoneNumber"
                              label="Phone Number"
                              value={form.phoneNumber}
                              onChange={handleChange}
                              error={Boolean(fieldErrors.phoneNumber)}
                              helperText={fieldErrors.phoneNumber || "Very important"}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              name="email"
                              label="Email (optional)"
                              value={form.email}
                              onChange={handleChange}
                              error={Boolean(fieldErrors.email)}
                              helperText={fieldErrors.email || " "}
                            />
                          </Grid>
                        </Grid>
                      </StepSectionCard>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <StepSectionCard
                        title="Resident Info"
                        subtitle="Enter a ZIP code or choose city and state so we can route the request."
                      >
                        <Stack spacing={1.5}>
                          <CareResidentDetailsFields
                            value={{
                              residentAge: form.residentAge,
                              gender: form.gender,
                              currentSituation: form.currentSituation,
                              currentLocationText: "",
                              budget: form.budget,
                              privateRoomNeeded: form.privateRoomNeeded,
                              caregiverPreference: form.caregiverPreference,
                            }}
                            onChange={handleResidentDetailsFieldsChange}
                            showCurrentLocationText={false}
                            showBudget={false}
                            showPrivateRoomNeeded={false}
                            showCaregiverPreference={false}
                          />

                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl
                                fullWidth
                                size="small"
                                required
                                error={Boolean(fieldErrors.currentCountryId)}
                              >
                                <InputLabel id="current-country-label">Current Country</InputLabel>
                                <Select
                                  labelId="current-country-label"
                                  label="Current Country"
                                  value={asSelectValue(form.currentCountryId)}
                                  onChange={(event: SelectChangeEvent<string>) => {
                                    const nextCountryId = event.target.value
                                      ? Number(event.target.value)
                                      : null;
                                    setForm((prev) => ({
                                      ...prev,
                                      currentCountryId: nextCountryId,
                                      currentStateId: null,
                                      currentCityId: null,
                                    }));
                                    setFieldErrors((prev) => ({
                                      ...prev,
                                      currentCountryId: "",
                                      currentStateId: "",
                                      currentCityId: "",
                                    }));
                                    setError(null);
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Select a country</em>
                                  </MenuItem>
                                  {countries.map((country: Country) => (
                                    <MenuItem key={String(country.id)} value={String(country.id)}>
                                      {country.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <FormHelperText>{fieldErrors.currentCountryId || " "}</FormHelperText>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl
                                fullWidth
                                size="small"
                                required={currentHasStates && !form.currentZipCode.trim()}
                                disabled={!currentHasStates}
                                error={Boolean(fieldErrors.currentStateId)}
                              >
                                <InputLabel id="current-state-label">Current State</InputLabel>
                                <Select
                                  labelId="current-state-label"
                                  label="Current State"
                                  value={asSelectValue(form.currentStateId)}
                                  onChange={(event: SelectChangeEvent<string>) => {
                                    const nextStateId = event.target.value
                                      ? Number(event.target.value)
                                      : null;
                                    setForm((prev) => ({
                                      ...prev,
                                      currentStateId: nextStateId,
                                      currentCityId: null,
                                    }));
                                    setFieldErrors((prev) => ({
                                      ...prev,
                                      currentStateId: "",
                                      currentCityId: "",
                                    }));
                                    setError(null);
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>{currentHasStates ? "Select a state" : "Select a country first"}</em>
                                  </MenuItem>
                                  {currentCountry?.states?.map((state) => (
                                    <MenuItem key={String(state.id)} value={String(state.id)}>
                                      {state.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <FormHelperText>
                                  {fieldErrors.currentStateId ||
                                    (currentHasStates
                                      ? form.currentZipCode.trim()
                                        ? "Optional with ZIP."
                                        : " "
                                      : "Select a country first.")}
                                </FormHelperText>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl
                                fullWidth
                                size="small"
                                required={currentHasCities && !form.currentZipCode.trim()}
                                disabled={!currentHasCities}
                                error={Boolean(fieldErrors.currentCityId)}
                              >
                                <InputLabel id="current-city-label">Current City</InputLabel>
                                <Select
                                  labelId="current-city-label"
                                  label="Current City"
                                  value={asSelectValue(form.currentCityId)}
                                  onChange={(event: SelectChangeEvent<string>) => {
                                    handleLocationSelectChange(
                                      "currentCityId",
                                      event.target.value ? Number(event.target.value) : null,
                                      ["currentCityId"]
                                    );
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>{currentHasCities ? "Select a city" : "Select a state first"}</em>
                                  </MenuItem>
                                  {currentState?.cities?.map((city) => (
                                    <MenuItem key={String(city.id)} value={String(city.id)}>
                                      {city.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <FormHelperText>
                                  {fieldErrors.currentCityId ||
                                    (currentHasCities
                                      ? form.currentZipCode.trim()
                                        ? "Optional with ZIP."
                                        : " "
                                      : "Select a state first.")}
                                </FormHelperText>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                required={!form.currentCityId}
                                name="currentZipCode"
                                label="Current ZIP / Postal Code"
                                value={form.currentZipCode}
                                onChange={(event) =>
                                  handleLocationTextChange("currentZipCode", event.target.value, [
                                    "currentZipCode",
                                  ])
                                }
                                error={Boolean(fieldErrors.currentZipCode)}
                                helperText={
                                  fieldErrors.currentZipCode ||
                                  (form.currentCityId
                                    ? "Optional if city is selected."
                                    : "Used to route faster.")
                                }
                              />
                            </Grid>
                          </Grid>
                        </Stack>
                      </StepSectionCard>
                    </Grid>
                  </Grid>
                ) : null}

                {activeStep === 1 ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <StepSectionCard
                        title="Care Needs"
                        subtitle="Tell us what kind of help is needed."
                      >
                        <FormControl component="fieldset" variant="standard">
                          <FormLabel component="legend">Select all that apply</FormLabel>
                          <FormGroup
                            sx={{
                              mt: 1,
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                              columnGap: 1.5,
                            }}
                          >
                            {CARE_NEED_OPTIONS.map((option) => (
                              <FormControlLabel
                                key={option}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={form.careNeeds.includes(option)}
                                    onChange={handleCareNeedChange}
                                    name="careNeeds"
                                    value={option}
                                  />
                                }
                                label={option}
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={3}
                          name="notes"
                          label="Anything else we should know?"
                          value={form.notes}
                          onChange={handleChange}
                        />
                      </StepSectionCard>
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <StepSectionCard
                        title="Payment & Timeline"
                        subtitle="Add payment details and when care is needed."
                      >
                        <TextField
                          select
                          fullWidth
                          size="small"
                          required
                          name="payerType"
                          label="Payer / Funding Source"
                          value={form.payerType}
                          onChange={handlePayerTypeChange}
                          error={Boolean(fieldErrors.payerType)}
                          helperText={fieldErrors.payerType || " "}
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          {PAYER_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>

                        <CareResidentDetailsFields
                          value={{
                            residentAge: form.residentAge,
                            gender: form.gender,
                            currentSituation: form.currentSituation,
                            currentLocationText: "",
                            budget: form.budget,
                            privateRoomNeeded: form.privateRoomNeeded,
                            caregiverPreference: form.caregiverPreference,
                          }}
                          onChange={handleResidentDetailsFieldsChange}
                          showResidentAge={false}
                          showGender={false}
                          showCurrentSituation={false}
                          showCurrentLocationText={false}
                          showPrivateRoomNeeded={false}
                          showCaregiverPreference={false}
                          budgetLabel={isMedicaidRequest ? "Budget (optional)" : "Budget"}
                          budgetError={Boolean(fieldErrors.budget)}
                          budgetHelperText={
                            fieldErrors.budget ||
                            (isMedicaidRequest ? "Optional for Medicaid requests." : " ")
                          }
                        />

                        <FormControl error={Boolean(fieldErrors.timeline)}>
                          <FormLabel>When is care needed?</FormLabel>
                          <RadioGroup
                            row
                            name="timeline"
                            value={form.timeline}
                            onChange={handleChange}
                            sx={{
                              mt: 1,
                              gap: 1,
                              "& .MuiFormControlLabel-root": { mr: 1.5 },
                            }}
                          >
                            {TIMELINE_OPTIONS.map((option) => (
                              <FormControlLabel
                                key={option}
                                value={option}
                                control={<Radio size="small" />}
                                label={option}
                              />
                            ))}
                          </RadioGroup>
                          <Typography variant="caption" color="error">
                            {fieldErrors.timeline || " "}
                          </Typography>
                        </FormControl>
                      </StepSectionCard>
                    </Grid>
                  </Grid>
                ) : null}

                {activeStep === 2 ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <StepSectionCard
                        title="Preferences"
                        subtitle="Add any preferred area or room preferences."
                      >
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel id="preferred-country-label">Preferred Country</InputLabel>
                              <Select
                                labelId="preferred-country-label"
                                label="Preferred Country"
                                value={asSelectValue(form.preferredCountryId)}
                                onChange={(event: SelectChangeEvent<string>) => {
                                  const nextCountryId = event.target.value
                                    ? Number(event.target.value)
                                    : null;
                                  setForm((prev) => ({
                                    ...prev,
                                    preferredCountryId: nextCountryId,
                                    preferredStateId: null,
                                    preferredCityId: null,
                                  }));
                                  setError(null);
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select a country</em>
                                </MenuItem>
                                {countries.map((country: Country) => (
                                  <MenuItem key={String(country.id)} value={String(country.id)}>
                                    {country.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>Optional</FormHelperText>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small" disabled={!preferredHasStates}>
                              <InputLabel id="preferred-state-label">Preferred State</InputLabel>
                              <Select
                                labelId="preferred-state-label"
                                label="Preferred State"
                                value={asSelectValue(form.preferredStateId)}
                                onChange={(event: SelectChangeEvent<string>) => {
                                  const nextStateId = event.target.value
                                    ? Number(event.target.value)
                                    : null;
                                  setForm((prev) => ({
                                    ...prev,
                                    preferredStateId: nextStateId,
                                    preferredCityId: null,
                                  }));
                                  setError(null);
                                }}
                              >
                                <MenuItem value="">
                                  <em>{preferredHasStates ? "Select a state" : "Select a country first"}</em>
                                </MenuItem>
                                {preferredCountry?.states?.map((state) => (
                                  <MenuItem key={String(state.id)} value={String(state.id)}>
                                    {state.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>
                                {preferredHasStates ? "Optional" : "Select a country first."}
                              </FormHelperText>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small" disabled={!preferredHasCities}>
                              <InputLabel id="preferred-city-label">Preferred City</InputLabel>
                              <Select
                                labelId="preferred-city-label"
                                label="Preferred City"
                                value={asSelectValue(form.preferredCityId)}
                                onChange={(event: SelectChangeEvent<string>) => {
                                  handleLocationSelectChange(
                                    "preferredCityId",
                                    event.target.value ? Number(event.target.value) : null
                                  );
                                }}
                              >
                                <MenuItem value="">
                                  <em>{preferredHasCities ? "Select a city" : "Select a state first"}</em>
                                </MenuItem>
                                {preferredState?.cities?.map((city) => (
                                  <MenuItem key={String(city.id)} value={String(city.id)}>
                                    {city.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>
                                {preferredHasCities ? "Optional" : "Select a state first."}
                              </FormHelperText>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              name="preferredZipCode"
                              label="Preferred ZIP / Postal Code"
                              value={form.preferredZipCode}
                              onChange={(event) =>
                                handleLocationTextChange("preferredZipCode", event.target.value)
                              }
                              helperText="Optional"
                            />
                          </Grid>
                        </Grid>

                        <CareResidentDetailsFields
                          value={{
                            residentAge: form.residentAge,
                            gender: form.gender,
                            currentSituation: form.currentSituation,
                            currentLocationText: "",
                            budget: form.budget,
                            privateRoomNeeded: form.privateRoomNeeded,
                            caregiverPreference: form.caregiverPreference,
                          }}
                          onChange={handleResidentDetailsFieldsChange}
                          showResidentAge={false}
                          showGender={false}
                          showCurrentSituation={false}
                          showCurrentLocationText={false}
                          showBudget={false}
                        />
                      </StepSectionCard>
                    </Grid>
                  </Grid>
                ) : null}

                {activeStep === 3 ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <StepSectionCard
                        title="Review"
                        subtitle="Take one more look before sending the request."
                      >
                        <Alert severity="info" sx={{ borderRadius: 3 }}>
                          After you submit, {currentFollowUpMessage}
                        </Alert>
                        <Grid container spacing={1.5}>
                          {finalReviewSummary.map((item) => (
                            <Grid item xs={12} sm={6} md={4} key={item.label}>
                              <Paper
                                elevation={0}
                                sx={{
                                  height: "100%",
                                  borderRadius: 3,
                                  border: "1px solid rgba(20,50,74,0.10)",
                                  p: 1.5,
                                  bgcolor: "#ffffff",
                                }}
                              >
                                <Typography
                                  variant="overline"
                                  sx={{ fontWeight: 800, color: "#607a89" }}
                                >
                                  {item.label}
                                </Typography>
                                <Typography sx={{ color: "#14324a", fontWeight: 700 }}>
                                  {item.value}
                                </Typography>
                              </Paper>
                            </Grid>
                          ))}
                          {form.notes.trim() ? (
                            <Grid item xs={12}>
                              <Paper
                                elevation={0}
                                sx={{
                                  borderRadius: 3,
                                  border: "1px solid rgba(20,50,74,0.10)",
                                  p: 1.5,
                                  bgcolor: "#ffffff",
                                }}
                              >
                                <Typography
                                  variant="overline"
                                  sx={{ fontWeight: 800, color: "#607a89" }}
                                >
                                  Notes
                                </Typography>
                                <Typography sx={{ color: "#14324a" }}>
                                  {form.notes.trim()}
                                </Typography>
                              </Paper>
                            </Grid>
                          ) : null}
                        </Grid>
                        <Box
                          sx={{
                            mt: 0.5,
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 3,
                            border: "1px solid rgba(20,50,74,0.10)",
                            bgcolor: "rgba(13,91,102,0.04)",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={reviewConfirmed}
                                onChange={(event) => {
                                  setReviewConfirmed(event.target.checked);
                                  if (event.target.checked) {
                                    setError(null);
                                  }
                                }}
                              />
                            }
                            label="I reviewed these details and I’m ready to submit this referral."
                            sx={{ m: 0, alignItems: "flex-start" }}
                          />
                          {!reviewConfirmed ? (
                            <Typography
                              variant="caption"
                              sx={{ display: "block", mt: 0.5, color: "#607a89" }}
                            >
                              Check this before submitting.
                            </Typography>
                          ) : null}
                        </Box>
                      </StepSectionCard>
                    </Grid>
                  </Grid>
                ) : null}

                {!submitted ? (
                  <Stack
                    direction={{ xs: "column-reverse", sm: "row" }}
                    spacing={1.25}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    {activeStep > 0 ? (
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={handlePreviousStep}
                        disabled={submitting}
                        sx={{
                          borderRadius: 999,
                          px: 2.25,
                          py: 1,
                          textTransform: "none",
                          fontWeight: 700,
                        }}
                      >
                        Back
                      </Button>
                    ) : (
                      <Box sx={{ display: { xs: "none", sm: "block" } }} />
                    )}

                    {isFinalStep ? (
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={submitting || !reviewConfirmed}
                        sx={{
                          py: 1.15,
                          px: 2.5,
                          borderRadius: 999,
                          bgcolor: "#0f7a5a",
                          fontWeight: 800,
                          letterSpacing: "0.01em",
                          "&:hover": { bgcolor: "#0d664c" },
                        }}
                      >
                        {submitting ? "Submitting..." : "Submit referral"}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="contained"
                        size="large"
                        onClick={handleNextStep}
                        sx={{
                          py: 1.15,
                          px: 2.5,
                          borderRadius: 999,
                          bgcolor: "#0d5b66",
                          fontWeight: 800,
                          letterSpacing: "0.01em",
                          "&:hover": { bgcolor: "#0a4b54" },
                        }}
                      >
                        {activeStep === totalSteps - 2 ? "Continue to review" : "Continue"}
                      </Button>
                    )}
                  </Stack>
                ) : null}
                {submitted ? (
                  <Typography
                    variant="body2"
                    sx={{ color: "#0f7a5a", fontWeight: 700, textAlign: "center" }}
                  >
                    Request sent. {currentFollowUpMessage}
                    {submissionReference ? ` Reference: ${submissionReference}` : ""}
                  </Typography>
                ) : null}

                {activeStep === totalSteps - 1 || submitted ? (
                  <Box sx={{ pt: 1 }}>
                    <PublicShareActions
                      title="Share this referral form"
                      description="Send this form to a family member or anyone helping with the resident placement search."
                      message={socialShareMessage}
                      shareUrl={shareUrl}
                    />
                  </Box>
                ) : null}
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Stack spacing={0.35}>
      <Typography variant="overline" sx={{ fontWeight: 800, color: "#5d7888" }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" sx={{ color: "#607a89" }}>
          {subtitle}
        </Typography>
      ) : null}
    </Stack>
  );
}

export { buildCareFindEmailText, buildCustomerConfirmationText };
